import { hasValue } from "@repo/helpers";
import { KeyedSetArray } from "@repo/models";
import { useCallback } from "react";

import { traverseProperty, traversePropertyUpdate } from "./dynamicProperties";

export type updatePropertyFn<TData> = (
	id: string,
	value: any,
	newState: TData,
	parentComponent?: string,
	dontEdit?: boolean,
) => Promise<TData>;

export function updateValuesWrapper<TData>(customChangesFn?: updatePropertyFn<TData>) {
	return async function (id: string, value: any, newState: TData, parentComponent?: string, dontEdit?: boolean) {
		const oldValue = traverseProperty(id, newState);
		if (oldValue === value) return { changed: false, newState };
		newState = traversePropertyUpdate(id, newState, value);
		const changed = isChanged(oldValue, value);

		if (changed && customChangesFn)
			newState = await customChangesFn(id, value, newState, parentComponent, dontEdit);

		return { changed, newState };
	};

	function isChanged<T>(oldValue: T, value: T) {
		if (oldValue === value) return false;
		if (!hasValue(oldValue) && !hasValue(value)) return false;
		if (!hasValue(oldValue) || !hasValue(value)) return true;
		const isDate = oldValue instanceof Date;
		const isFile = oldValue instanceof File || value instanceof File;
		const isObject = typeof oldValue === "object" && !isDate && !isFile;
		if (isObject) {
			//If object, check child properties
			for (const prop of Object.keys({ ...oldValue, ...value })) {
				if (isChanged(oldValue[prop], value[prop])) return true;
			}
		}
		if (Array.isArray(oldValue)) {
			for (const i in oldValue) {
				if (isChanged(oldValue[i], value[i])) return true;
			}
		}
		return oldValue !== value;
	}
}

export function updateCopiedPropertiesHook<TData>(copiedProperties: KeyedSetArray<string>) {
	return async function (id: string, value: any, newState: TData, parentComponent?: string) {
		if (copiedProperties) {
			if (copiedProperties.has(id)) {
				//Not currently covered by new component
				copiedProperties
					.getValue(id)
					.forEach((name) => (newState = traversePropertyUpdate(name, newState, value)));
			}
			if (parentComponent && copiedProperties.has(parentComponent)) {
				const parentValue = {
					...(traverseProperty(parentComponent, newState) as any),
				};
				parentValue.matches = true;
				copiedProperties
					.getValue(parentComponent)
					.forEach((name) => (newState = traversePropertyUpdate(name, newState, parentValue)));
			} //Cloning to force dynamic components to at least check if they should be updated
		}
		return newState;
	};
}

export function useMultiplePropUpdates<TData>(...updateFns: updatePropertyFn<TData>[]) {
	async function updateAll(id: string, value: any, newState: TData, parentComponent?: string, dontEdit?: boolean) {
		for (const updateFn of updateFns) {
			newState = await updateFn(id, value, newState, parentComponent, dontEdit);
		}
		return newState;
	}
	return useCallback(updateAll, [...updateFns]) as updatePropertyFn<TData>;
}
